import { useCallback, useState } from 'react';
import { Card, Button, Tooltip } from '@agro1desenvolvimento/react-components';
import { TipoExigenciaDano } from './types';
import type useClassificacaoSementes from '../../../hooks/useClassificacaoSementes';
import type useTetrazolioConfiguracoes from '../../../hooks/useTetrazolioConfiguracoes';
import useFormularioClassificacao from '../../../hooks/useFormularioClassificacao';
import useUnsavedChangesWarning from '../../../hooks/useUnsavedChangesWarning';
import tetrazolioService from '../../../../../../services/sementes/tetrazolio';
import ModalConfirmacao from '../../Modais/ModalConfirmacao';
import ModalDecisao from '../../Modais/ModalDecisao';

interface Props {
  classificacao: ReturnType<typeof useClassificacaoSementes>;
  configuracoes: ReturnType<typeof useTetrazolioConfiguracoes>;
  analiseId: string;
  status: string;
  idDaAmostra: string;
  concluidoApi?: boolean;
  onConcluir?: () => void | Promise<void>;
}

export default function FormularioTetrazolioClassificacao({
  classificacao,
  configuracoes,
  analiseId,
  status,
  idDaAmostra,
  concluidoApi = false,
  onConcluir,
}: Props) {
  const { graus, danos, loading: configLoading } = configuracoes;
  const [sincronizando, setSincronizando] = useState(false);
  const [modalRejeitarVisible, setModalRejeitarVisible] = useState(false);
  const [
    modalSincronizacaoSucessoVisible,
    setModalSincronizacaoSucessoVisible,
  ] = useState(false);
  const [modalSincronizacaoErroVisible, setModalSincronizacaoErroVisible] = useState(false);
  const [modalConfirmarConclusaoVisible, setModalConfirmarConclusaoVisible] = useState(false);
  const [modalAnaliseConcluidaVisible, setModalAnaliseConcluidaVisible] = useState(false);

  const {
    lancamentos,
    estatisticas,
    repeticaoAtual,
    totalRepeticoes,
    podeAvancarRepeticao,
    podeConcluirAnalise,
    removerLancamento,
    proximaRepeticao,
    todosLancamentos,
    pendingRejeitarRepeticao,
    confirmarTrocaRepeticaoRejeitada,
    cancelarTrocaRepeticaoRejeitada,
    getLancamentosPorRepeticaoParaAPI,
    repeticaoEstaCompleta,
    temDadosNaoSincronizados,
    marcarComoSincronizado,
  } = classificacao;

  useUnsavedChangesWarning(temDadosNaoSincronizados);

  const {
    formik,
    handleGrauClick,
    handleDanoClick,
    getDanoButtonClass,
    getGrauButtonClass,
    grauConfig,
    erro,
    podelancar,
    modalDanoIntensoVisible,
    fecharModalDanoIntenso,
  } = useFormularioClassificacao({ classificacao, configuracoes });

  const handleRejeitarAmostragem = () => {
    if (classificacao.repeticaoRejeitada !== null) {
      classificacao.rejeitarRepeticao(repeticaoAtual, false);
      formik.resetForm();
      return;
    }

    setModalRejeitarVisible(true);
  };

  const confirmarRejeitarAmostragem = async () => {
    try {
      setSincronizando(true);

      classificacao.rejeitarRepeticao(repeticaoAtual, true);
      formik.resetForm();
      setModalRejeitarVisible(false);

      await tetrazolioService.updateRepeticaoRejeitada(
        analiseId,
        repeticaoAtual,
      );
      // Pequena espera para evitar a race condition no backend
      await new Promise((resolve) => setTimeout(resolve, 500));
      await tetrazolioService.submitResultados(
        analiseId,
        getLancamentosPorRepeticaoParaAPI(),
      );

      marcarComoSincronizado();
    } catch (error) {
      setModalSincronizacaoErroVisible(true);
      console.error('Erro ao sincronizar após rejeitar:', error);
    } finally {
      setSincronizando(false);
    }
  };

  const cancelarRejeitarAmostragem = () => {
    setModalRejeitarVisible(false);
  };

  const handleConfirmarTrocaRepeticaoRejeitada = async () => {
    if (!pendingRejeitarRepeticao) return;

    try {
      setSincronizando(true);

      confirmarTrocaRepeticaoRejeitada();

      await Promise.all([
        tetrazolioService.updateRepeticaoRejeitada(
          analiseId,
          pendingRejeitarRepeticao.numeroRepeticao,
        ),
        tetrazolioService.submitResultados(
          analiseId,
          getLancamentosPorRepeticaoParaAPI(),
        ),
      ]);

      marcarComoSincronizado();
    } catch (error) {
      setModalSincronizacaoErroVisible(true);
      console.error('Erro ao sincronizar troca de repetição rejeitada:', error);
    } finally {
      setSincronizando(false);
    }
  };

  const handleSincronizarDados = async () => {
    try {
      setSincronizando(true);

      const lancamentosParaAPI = getLancamentosPorRepeticaoParaAPI();
      await tetrazolioService.submitResultados(analiseId, lancamentosParaAPI);

      marcarComoSincronizado();
      setModalSincronizacaoSucessoVisible(true);
    } catch (error) {
      setModalSincronizacaoErroVisible(true);
      console.error('Erro ao sincronizar:', error);
    } finally {
      setSincronizando(false);
    }
  };

  const handleProximaRepeticao = async () => {
    try {
      setSincronizando(true);

      const lancamentosParaAPI = getLancamentosPorRepeticaoParaAPI();
      await tetrazolioService.submitResultados(analiseId, lancamentosParaAPI);

      marcarComoSincronizado();
      proximaRepeticao();
    } catch (error) {
      setModalSincronizacaoErroVisible(true);
      console.error('Erro ao sincronizar:', error);
    } finally {
      setSincronizando(false);
    }
  };

  const handleConcluirAnalise = () => {
    // Mostrar modal de confirmação antes de concluir
    setModalConfirmarConclusaoVisible(true);
  };

  const confirmarConclusao = async () => {
    setModalConfirmarConclusaoVisible(false);

    try {
      setSincronizando(true);

      const lancamentosParaAPI = getLancamentosPorRepeticaoParaAPI();
      await tetrazolioService.submitResultados(analiseId, lancamentosParaAPI, false);
      await tetrazolioService.concluirAnalise(analiseId);

      marcarComoSincronizado();
      if (onConcluir) await onConcluir();
      setModalAnaliseConcluidaVisible(true);
    } catch (error) {
      setModalSincronizacaoErroVisible(true);
      console.error('Erro ao sincronizar:', error);
    } finally {
      setSincronizando(false);
    }
  };

  const cancelarConclusao = () => {
    setModalConfirmarConclusaoVisible(false);
  };

  const proximaRepeticaoButtonMessage = useCallback(() => {
    if (sincronizando) return 'Sincronizando...';
    if (podeConcluirAnalise) return 'Concluir Análise';
    return 'Próxima Repetição →';
  }, [sincronizando, podeConcluirAnalise]);

  if (configLoading) {
    return <div>Carregando configurações...</div>;
  }

  const isDanosButtonDisabled = classificacao.repeticaoEstaRejeitada
    || repeticaoEstaCompleta
    || !grauConfig
    || (grauConfig.exigenciaDano !== TipoExigenciaDano.NORMAL
      && grauConfig.exigenciaDano !== TipoExigenciaDano.INTENSO);

  const isSubmitButtonDisabled = !podelancar
    || classificacao.repeticaoEstaRejeitada
    || repeticaoEstaCompleta;

  const onClickConcluirAnalise = () => {
    if (podeConcluirAnalise) {
      handleConcluirAnalise();
      return;
    }
    handleProximaRepeticao();
  };

  return (
    <div className="formulario-tetrazolio-classificacao">
      <div className="repeticoes-tabs">
        {Array.from({ length: totalRepeticoes }, (_, i) => i + 1).map((num) => {
          const isActive = num === repeticaoAtual;
          const isRejeitada = classificacao.repeticaoRejeitada === num;

          const lancamentosDestaRepeticao = classificacao.todosLancamentos.filter(
            (lancamento) => lancamento.repeticao === num,
          );
          const isCompleted = lancamentosDestaRepeticao.length
            === classificacao.sementesPorRepeticao;
          const isIncomplete = !isCompleted && !isRejeitada;

          return (
            <div
              key={num}
              className={`repeticao-tab ${isActive ? 'active' : ''} ${
                isCompleted ? 'completed' : ''
              } ${isIncomplete ? 'incomplete' : ''} ${
                isRejeitada ? 'rejeitada' : ''
              }`}
              onClick={() => classificacao.irParaRepeticao(num)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  classificacao.irParaRepeticao(num);
                }
              }}
            >
              <span>Repetição {num}</span>
              {isRejeitada && (
                <i className="fas fa-times-circle" style={{ color: 'red' }} />
              )}
              {!isRejeitada && isCompleted && (
                <i className="fas fa-check-circle" style={{ color: 'green' }} />
              )}
              {!isRejeitada && isIncomplete && (
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: 'orange' }}
                />
              )}
            </div>
          );
        })}
      </div>

      <Card className="classificacao-form">
        <h3>Classificar Sementes</h3>

        <div className="status-badge ativa">Ativa</div>

        <div className="estatisticas-card">
          <div className="estatistica">
            <label>Sementes Lançadas</label>
            <span>{estatisticas.sementesLancadas}</span>
          </div>
          <div className="estatistica">
            <label>Vigor</label>
            <span>{estatisticas.vigor}</span>
          </div>
          <div className="estatistica">
            <label>Viabilidade</label>
            <span>{estatisticas.viabilidade}</span>
          </div>
          {classificacao.repeticaoEstaRejeitada ? (
            <>
              <i className="fas fa-info-circle info-reativar-icon" />
              <Tooltip
                target=".info-reativar-icon"
                content="Para reativar essa repetição, é necessário rejeitar outra repetição"
                position="top"
              />
            </>
          ) : (
            <Button
              type="button"
              className="rejeitar-button"
              onClick={handleRejeitarAmostragem}
              disabled={status === 'Realizado'}
            >
              Rejeitar Repetição
            </Button>
          )}
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="grau-section">
            <label>Grau</label>
            <div className="grau-buttons">
              {graus.map((config) => (
                <Button
                  key={config.id}
                  type="button"
                  label={config.descricao}
                  className={getGrauButtonClass(config.id)}
                  onClick={() => handleGrauClick(config.id)}
                  disabled={
                    classificacao.repeticaoEstaRejeitada
                    || repeticaoEstaCompleta
                  }
                />
              ))}
            </div>

            <div className="legenda">
              <div className="legenda-item">
                <div className="legenda-cor nao-usa-dano" />
                <span>Não usa dano</span>
              </div>
              <div className="legenda-item">
                <div className="legenda-cor exige-dano" />
                <span>Exigem dano</span>
              </div>
              <div className="legenda-item">
                <div className="legenda-cor exige-dano-intenso" />
                <span>Exigem dano intenso</span>
              </div>
            </div>

            <div className="grau-info">
              Selecione 2x para classificar como dano intenso
            </div>
          </div>

          <div className="dano-section">
            <label>Dano</label>
            <div className="dano-content">
              <div className="dano-buttons">
                {danos.map((opcao) => (
                  <Button
                    key={opcao.tipo}
                    type="button"
                    className={getDanoButtonClass(opcao.tipo)}
                    onClick={() => handleDanoClick(opcao.tipo)}
                    disabled={isDanosButtonDisabled}
                  >
                    {opcao.label}
                  </Button>
                ))}
              </div>

              <div className="lancar-section">
                <Button
                  type="submit"
                  className={`lancar-button ${
                    podelancar ? 'ativo' : 'inativo'
                  }`}
                  disabled={isSubmitButtonDisabled}
                >
                  Lançar
                </Button>
                <span className="erro-message">{erro || ''}</span>
              </div>
            </div>
          </div>
        </form>
      </Card>

      {lancamentos.length > 0 && (
        <Card className="lancamentos-lista">
          <div className="lancamentos-header-row">
            <h4>Lançamentos</h4>
            <Button
              type="button"
              className="sincronizar-button"
              onClick={handleSincronizarDados}
              disabled={sincronizando || todosLancamentos.length === 0 || status === 'Realizado'}
            >
              {sincronizando ? 'Sincronizando...' : 'Sincronizar Dados'}
            </Button>
          </div>
          <div className="lancamentos-header">
            <span>Contagem</span>
            <span>Grau</span>
            <span>Dano</span>
            <span />
          </div>
          <div className="lancamentos-cards">
            {lancamentos.map((lancamento) => {
              const grauConfigLancamento = graus.find((grau) => grau.id === lancamento.grauId);
              let exigenciaClass = '';
              if (grauConfigLancamento) {
                if (grauConfigLancamento.exigenciaDano === TipoExigenciaDano.NENHUM) {
                  exigenciaClass = 'nao-usa-dano';
                } else if (grauConfigLancamento.exigenciaDano === TipoExigenciaDano.INTENSO) {
                  exigenciaClass = 'exige-dano-intenso';
                } else if (grauConfigLancamento.exigenciaDano === TipoExigenciaDano.NORMAL) {
                  exigenciaClass = 'exige-dano';
                }
              }

              return (
                <div key={lancamento.id} className="lancamento-card">
                  <div className="contagem">{lancamento.contagem}</div>
                  <div className={`grau-badge ${exigenciaClass}`}>
                    {grauConfigLancamento?.descricao || lancamento.grau}
                  </div>
                  <div className="danos-list">
                    {lancamento.danos.length > 0 ? (
                      lancamento.danos.map((d, idx) => {
                        const label = danos.find((opt) => opt.tipo === d.tipo)?.label;
                        const isIntenso = d.intensidade === 'intenso';
                        return (
                          <span key={idx}>
                            {idx > 0 && ' | '}
                            {isIntenso ? <strong>{label}</strong> : label}
                          </span>
                        );
                      })
                    ) : (
                      '-'
                    )}
                  </div>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => removerLancamento(lancamento.id)}
                    disabled={classificacao.repeticaoEstaRejeitada || status === 'Realizado'}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {(podeAvancarRepeticao || podeConcluirAnalise) && status !== 'Realizado' && !concluidoApi && (
        <div
          className="proxima-repeticao-overlay"
          style={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            padding: '20px 0',
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            type="button"
            className="proxima-repeticao-button"
            onClick={onClickConcluirAnalise}
            disabled={sincronizando}
            style={{ width: '50%' }}
          >
            {proximaRepeticaoButtonMessage()}
          </Button>
        </div>
      )}

      <ModalConfirmacao
        visible={modalDanoIntensoVisible}
        title="Informe um dano intenso"
        description="Você deve selecionar no mínimo um dano 2x para classifica-lo como dano intenso"
        onConfirm={fecharModalDanoIntenso}
      />

      <ModalDecisao
        visible={modalRejeitarVisible}
        title="Rejeitar repetição?"
        description="Tem certeza que deseja rejeitar a repetição selecionada?"
        okButtonLabel="Sim, rejeitar"
        cancelButtonLabel="Não"
        onConfirm={confirmarRejeitarAmostragem}
        onCancel={cancelarRejeitarAmostragem}
      />

      <ModalConfirmacao
        visible={modalSincronizacaoSucessoVisible}
        title="Dados sincronizados"
        description="Dados sincronizados com sucesso!"
        onConfirm={() => setModalSincronizacaoSucessoVisible(false)}
        buttonLabel="Confirmar"
      />

      <ModalConfirmacao
        visible={modalSincronizacaoErroVisible}
        title="Erro na sincronização"
        description="Erro ao sincronizar dados. Tente novamente."
        onConfirm={() => setModalSincronizacaoErroVisible(false)}
      />

      <ModalDecisao
        visible={modalConfirmarConclusaoVisible}
        title="Concluir repetições?"
        description={`Deseja concluir as repetições da análise de sementes da amostra (Nº ${idDaAmostra})? O status da análise mudará para REALIZADO.`}
        okButtonLabel="Confirmar"
        cancelButtonLabel="Fechar"
        onConfirm={confirmarConclusao}
        onCancel={cancelarConclusao}
      />

      <ModalConfirmacao
        visible={modalAnaliseConcluidaVisible}
        title="Repetições concluídas"
        description={`As repetições da análise de sementes da amostra (Nº ${idDaAmostra}) foram concluídas e seu status mudou para REALIZADO.`}
        onConfirm={() => setModalAnaliseConcluidaVisible(false)}
        buttonLabel="Ok"
      />

      <ModalDecisao
        visible={!!pendingRejeitarRepeticao}
        title="Trocar repetição rejeitada?"
        description={
          pendingRejeitarRepeticao
            ? `A repetição ${pendingRejeitarRepeticao.repeticaoJaRejeitada} já está rejeitada. Deseja reativá-la e rejeitar a repetição ${pendingRejeitarRepeticao.numeroRepeticao}?`
            : ''
        }
        okButtonLabel="Sim, trocar"
        cancelButtonLabel="Não"
        onConfirm={handleConfirmarTrocaRepeticaoRejeitada}
        onCancel={cancelarTrocaRepeticaoRejeitada}
      />
    </div>
  );
}
